import {
  ChartSuccessOutlineIcon,
  DocumentTextOutlineIcon,
  NoteOutlineIcon,
  PeopleOutlineIcon,
  RankingOutlineIcon,
  UserSearchOutlineIcon,
} from "assets/website";

type IconComponent = React.FC<React.SVGProps<SVGSVGElement>>;

interface CreditAction {
  Icon: IconComponent;
  title: string;
  unit: string;
  description: string;
}

const CREDIT_ACTIONS: CreditAction[] = [
  {
    Icon: DocumentTextOutlineIcon,
    title: "CV & Resume Analysis",
    unit: "Per CV evaluated",
    description:
      "Quickly evaluate any CV without requiring the candidate to complete an assessment.",
  },
  {
    Icon: NoteOutlineIcon,
    title: "Generate Assessment",
    unit: "Per custom test suite",
    description:
      "Generate custom technical, SJT (Situational Judgment), and behavioral questions adapted to seniorities.",
  },
  {
    Icon: PeopleOutlineIcon,
    title: "Candidate Comparison",
    unit: "Per comparison query",
    description:
      "Compare shortlisted candidates side-by-side across competencies, behavioural traits, and overall job fit.",
  },
  {
    Icon: UserSearchOutlineIcon,
    title: "Candidate Evaluation",
    unit: "Per completed report",
    description:
      "Receive a comprehensive AI hiring report covering competence, behavioural fit, personality, culture fit, strengths, risks, and hiring recommendations.",
  },
  {
    Icon: ChartSuccessOutlineIcon,
    title: "Applicant Pool Analytics",
    unit: "Per cohort analysis",
    description:
      "Identify hiring trends, strengths, weaknesses, and overall quality across your applicant pool.",
  },
  {
    Icon: RankingOutlineIcon,
    title: "Strength Gap Analysis",
    unit: "Per role benchmarked",
    description:
      "Understand team capability gaps and identify candidates who best complement your existing workforce.",
  },
];

/** The AI actions credits are spent on — the "what am I paying for" section. */
const CreditActions = () => (
  <section className="py-16 md:py-20 bg-[#F9FAFB]">
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12 md:mb-16">
        <h2 className="text-[28px] md:text-4xl lg:text-[52px] font-display font-semibold text-[#050B17] lg:leading-[1.1] tracking-[-0.5px] mb-4 md:mb-6">
          What Can You Do with Credits?
        </h2>
        <p className="text-base md:text-2xl text-[#475467] leading-[1.3] tracking-[-0.24px] max-w-[804px] mx-auto">
          Each AI task consumes a fixed, transparent credit amount based on raw compute complexity.
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {CREDIT_ACTIONS.map(({ Icon, title, unit, description }) => (
          <div
            key={title}
            className="bg-white border border-[#D1E9FF] rounded-[6px] px-5 py-6 shadow-[0px_4px_8px_0px_rgba(1,33,105,0.05)] flex flex-col"
          >
            <div className="border-b border-[#EFF8FF] pb-4 mb-4">
              <span className="inline-flex items-center bg-white border border-[#C6E0FF] rounded-[6px] p-2 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1)] mb-2">
                <Icon className="w-[18px] h-[18px]" aria-hidden="true" />
              </span>
              <h3 className="text-xl md:text-2xl font-semibold text-[#1D2939] leading-[1.3] tracking-[-0.25px]">
                {title}
              </h3>
              <p className="text-base text-[#475467] leading-6 mt-2">{unit}</p>
            </div>
            <p className="text-base text-[#475467] leading-[1.4] tracking-[-0.25px]">{description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export { CreditActions };
